/**
 * ゲームボードコンポーネント
 *
 * ゲーム全体のレイアウト統合（HTML参考: 3列グリッド + 4辺配置）
 * 固定サイズ（1400px × 1000px）で作成し、親要素でtransform: scale()でスケーリング
 */

import React, { useState, useEffect } from 'react';
import { useGameStore } from '@stores/gameStore';
import { Hand } from './Hand';
import { Field } from './Field';
import { ActionPanel } from './ActionPanel';

export const GameBoard: React.FC = () => {
  const { gameState, humanPlayerPosition, createGame, startNextRound, redistributionDecision } = useGameStore();

  // 手役モーダルを閉じたフラグ（ローカルステート）
  const [teyakuModalDismissed, setTeyakuModalDismissed] = useState(false);

  // 新しいラウンドが始まったらフラグをリセット
  useEffect(() => {
    if (gameState) {
      setTeyakuModalDismissed(false);
    }
  }, [gameState?.current_round]);

  // ゲーム開始処理
  const handleNewGame = (debugMode: boolean = false) => {
    createGame({
      human_player_position: 0,
      ai_types: ['vrc', 'vrc', 'vrc'],
      ai_levels: ['intermediate', 'intermediate', 'intermediate'],
      enable_special_rules: true,
      winning_points: 150,
      debug_mode: debugMode,
    });
  };

  if (!gameState) {
    return (
      <div className="w-[1020px] h-[1200px] bg-gradient-to-br from-gray-900 to-gray-800 text-white flex items-center justify-center">
        <div className="bg-gray-800 border-2 border-gray-700 rounded-2xl p-8 max-w-[400px] w-full mx-4">
          <h2 className="text-3xl font-bold text-center mb-6">ごいた</h2>
          <p className="text-center mb-6 text-gray-300">ゲームを開始してください</p>
          <div className="flex flex-col gap-3">
            <button
              onClick={() => handleNewGame(false)}
              className="w-full py-4 px-6 text-xl bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-bold rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              新しいゲームを開始
            </button>
            <button
              onClick={() => handleNewGame(true)}
              className="w-full py-4 px-6 text-xl bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white font-bold rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              🔍 デバッグモード
            </button>
          </div>
        </div>
      </div>
    );
  }

  const humanPlayer = gameState.players[humanPlayerPosition];

  // プレイヤーを位置で取得
  const getPlayer = (pos: number) => gameState.players[pos];

  // ターン表示
  const isTurn = (pos: number) => gameState.current_turn_position === pos;

  // 現在攻めているプレイヤーのポジション（親のポジション）
  const currentAttackPlayerPosition = gameState.current_parent_position;

  // プレイヤー名を簡略化（「AI2(intermediate)」→「AI」）
  // Fieldコンポーネントで「(P1)」が付加されるので、元の名前からレベル情報を削除
  const getSimplifiedPlayerName = (pos: number): string => {
    const player = getPlayer(pos);
    // 「(intermediate)」などのレベル部分と「(Pn)」部分を削除
    return player.name.replace(/\(.*?\)/g, '').trim();
  };

  return (
    <div className="w-[1020px] h-[1200px] bg-gradient-to-br from-gray-900 to-gray-800 text-white flex flex-col">
      {/* ヘッダー */}
      <header className="bg-gray-900 border-b border-gray-700 p-2 flex-shrink-0">
        <div className="max-w-[1020px] mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h1 className="text-xl font-bold">ごいた</h1>
            {/* バージョン表示（バグ報告用） */}
            <span className="text-xs opacity-50">v{__APP_VERSION__} / AI v{__AI_VERSION__}</span>
            {/* デバッグモードインジケーター */}
            {gameState.debug_mode && (
              <span className="bg-purple-600 px-3 py-1 rounded text-xs font-bold flex items-center gap-1">
                🔍 デバッグモード
              </span>
            )}
          </div>
          <div className="flex items-center gap-6">
            {/* ラウンド表示 */}
            <span className="text-sm opacity-75">ラウンド {gameState.current_round}</span>
            {/* チームスコア */}
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-xs opacity-75">青(P0+P2)</span>
                <span className="bg-blue-600 px-3 py-1 rounded font-bold">
                  {getPlayer(0).score}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs opacity-75">赤(P1+P3)</span>
                <span className="bg-red-600 px-3 py-1 rounded font-bold">
                  {getPlayer(1).score}
                </span>
              </div>
            </div>
            <span className="text-sm">目標: {gameState.winning_points}点</span>
          </div>
        </div>
      </header>

      {/* 再配布通知バナー（し5枚single/opponentsケース） */}
      {gameState.redistribution_notification && (
        <div className="bg-gradient-to-r from-yellow-600 to-orange-600 border-b-4 border-yellow-400 p-3 flex-shrink-0">
          <div className="max-w-[1020px] mx-auto flex items-center gap-3">
            <span className="text-2xl">⚠️</span>
            <p className="text-white font-bold text-lg">{gameState.redistribution_notification}</p>
          </div>
        </div>
      )}

      {/* メインエリア: 3列グリッド */}
      <main
        className="flex-1 grid justify-center p-2 gap-2"
        style={{
          gridTemplateColumns: '100px 800px 100px',
          gridTemplateRows: '113px 1fr 220px',
        }}
      >

        {/* 上段: P2の手駒 */}
        <section className="col-span-3 bg-gray-800 border border-gray-700 rounded-lg px-1 pt-1">
          <div className="text-xs opacity-75 mb-1">P2手駒</div>
          {gameState.debug_mode ? (
            <Hand
              pieces={getPlayer(2).hand}
              playerPosition={2}
              isHuman={false}
            />
          ) : (
            <div className="flex gap-2 flex-wrap justify-center">
              {Array.from({ length: getPlayer(2).hand_count }).map((_, i) => (
                <div key={i} className="w-16 h-20 bg-red-900 border border-red-700 rounded flex items-center justify-center text-xs">
                  伏
                </div>
              ))}
            </div>
          )}
        </section>

        {/* 中段左: P3の手駒 */}
        <section className="bg-gray-800 border border-gray-700 rounded-lg p-2 flex flex-col">
          <div className="text-xs opacity-75 mb-1">P3手駒</div>
          {gameState.debug_mode ? (
            <Hand
              pieces={getPlayer(3).hand}
              playerPosition={3}
              isHuman={false}
            />
          ) : (
            <div className="flex flex-col gap-2 items-end flex-1 overflow-auto">
              {Array.from({ length: getPlayer(3).hand_count }).map((_, i) => (
                <div key={i} className="w-20 h-16 bg-red-900 border border-red-700 rounded flex items-center justify-center text-xs">
                  伏
                </div>
              ))}
            </div>
          )}
        </section>

        {/* 中段中央: ボード（4辺配置） */}
        <section className="relative bg-gradient-to-br from-gray-800 to-gray-900 border-2 border-gray-700 rounded-2xl p-2">
          {/* 上辺: P2（3p）の場 */}
          <div className="absolute top-4 left-1/2 -translate-x-1/2">
            <div className={`${isTurn(2) ? 'ring-4 ring-yellow-400' : ''} transition-all duration-300`}>
              <Field
                pieces={getPlayer(2).field}
                playerPosition={2}
                playerName={getSimplifiedPlayerName(2)}
                isCurrentTurn={isTurn(2)}
                currentAttackPiece={gameState.current_attack_piece}
                currentAttackPlayerPosition={currentAttackPlayerPosition}
              />
            </div>
          </div>

          {/* 下辺: P0（1p）の場 */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2">
            <div className={`${isTurn(0) ? 'ring-4 ring-yellow-400' : ''} transition-all duration-300`}>
              <Field
                pieces={getPlayer(0).field}
                playerPosition={0}
                playerName={getSimplifiedPlayerName(0)}
                isCurrentTurn={isTurn(0)}
                currentAttackPiece={gameState.current_attack_piece}
                currentAttackPlayerPosition={currentAttackPlayerPosition}
              />
            </div>
          </div>

          {/* 左辺: P3（4p）の場 */}
          <div className="absolute left-4 top-1/2 -translate-y-1/2">
            <div className={`${isTurn(3) ? 'ring-4 ring-yellow-400' : ''} transition-all duration-300`}>
              <Field
                pieces={getPlayer(3).field}
                playerPosition={3}
                playerName={getSimplifiedPlayerName(3)}
                isCurrentTurn={isTurn(3)}
                currentAttackPiece={gameState.current_attack_piece}
                currentAttackPlayerPosition={currentAttackPlayerPosition}
              />
            </div>
          </div>

          {/* 右辺: P1（2p）の場 */}
          <div className="absolute right-4 top-1/2 -translate-y-1/2">
            <div className={`${isTurn(1) ? 'ring-4 ring-yellow-400' : ''} transition-all duration-300`}>
              <Field
                pieces={getPlayer(1).field}
                playerPosition={1}
                playerName={getSimplifiedPlayerName(1)}
                isCurrentTurn={isTurn(1)}
                currentAttackPiece={gameState.current_attack_piece}
                currentAttackPlayerPosition={currentAttackPlayerPosition}
              />
            </div>
          </div>

        </section>

        {/* 中段右: P1の手駒 */}
        <section className="bg-gray-800 border border-gray-700 rounded-lg p-2 flex flex-col">
          <div className="text-xs opacity-75 mb-1">P1手駒</div>
          {gameState.debug_mode ? (
            <Hand
              pieces={getPlayer(1).hand}
              playerPosition={1}
              isHuman={false}
            />
          ) : (
            <div className="flex flex-col gap-2 flex-1 overflow-auto">
              {Array.from({ length: getPlayer(1).hand_count }).map((_, i) => (
                <div key={i} className="w-20 h-16 bg-red-900 border border-red-700 rounded flex items-center justify-center text-xs">
                  伏
                </div>
              ))}
            </div>
          )}
        </section>

        {/* 下段: P0（1p・人間）の手駒 */}
        <section className="col-span-3 bg-gray-800 border-4 border-blue-500 rounded-lg p-1">
          <Hand
            pieces={humanPlayer.hand}
            playerPosition={humanPlayerPosition}
            isHuman={true}
          />
        </section>
      </main>

      {/* フッター: アクションパネル */}
      <footer className="bg-gray-900 border-t border-gray-700 p-2 flex-shrink-0">
        <div className="max-w-[1020px] mx-auto">
          <ActionPanel />
        </div>
      </footer>

      {/* ラウンド終了モーダル */}
      {gameState.phase === 'ROUND_END' && !gameState.is_game_over && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 border-4 border-green-400 rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl">
            {/* ラウンド終了タイトル */}
            <div className="text-center mb-6">
              <h2 className="text-4xl font-bold text-green-400 mb-2">🎊 ラウンド終了 🎊</h2>
              <div className="text-xl mt-4">
                <span className="text-gray-300">ラウンド {gameState.current_round} 終了</span>
              </div>
            </div>

            {/* スコア表示 */}
            <div className="bg-gray-900 rounded-lg p-6 mb-6 border border-gray-700">
              <h3 className="text-lg font-bold text-center mb-4 text-gray-300">現在のスコア</h3>
              <div className="space-y-3">
                {/* 青チームスコア */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full bg-blue-600"></div>
                    <span className="font-bold text-blue-400">青チーム (P0+P2)</span>
                  </div>
                  <span className="text-2xl font-bold text-blue-400">
                    {gameState.last_round_winner_team === 0 && gameState.last_round_points && (
                      <span className="text-green-400">(+{gameState.last_round_points}) </span>
                    )}
                    {getPlayer(0).score}点
                  </span>
                </div>

                {/* 赤チームスコア */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full bg-red-600"></div>
                    <span className="font-bold text-red-400">赤チーム (P1+P3)</span>
                  </div>
                  <span className="text-2xl font-bold text-red-400">
                    {gameState.last_round_winner_team === 1 && gameState.last_round_points && (
                      <span className="text-green-400">(+{gameState.last_round_points}) </span>
                    )}
                    {getPlayer(1).score}点
                  </span>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-gray-700 text-center">
                <span className="text-sm text-gray-400">目標: {gameState.winning_points}点</span>
              </div>
            </div>

            {/* 次のラウンドへボタン */}
            <button
              onClick={startNextRound}
              className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-bold py-4 px-6 rounded-lg text-xl transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              次のラウンドへ
            </button>
          </div>
        </div>
      )}

      {/* ゲーム終了モーダル */}
      {gameState.is_game_over && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 border-4 border-yellow-400 rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl">
            {/* 勝利タイトル */}
            <div className="text-center mb-6">
              <h2 className="text-4xl font-bold text-yellow-400 mb-2">🎉 ゲーム終了 🎉</h2>
              <div className="text-2xl font-bold mt-4">
                {gameState.winner_team === 0 ? (
                  <span className="text-blue-400">青チーム（P0+P2）の勝利！</span>
                ) : (
                  <span className="text-red-400">赤チーム（P1+P3）の勝利！</span>
                )}
              </div>
            </div>

            {/* 勝利理由表示 */}
            {gameState.win_reason_description && (
              <div className="bg-gradient-to-r from-yellow-900/30 to-yellow-800/30 rounded-lg p-4 mb-6 border border-yellow-600">
                <h3 className="text-lg font-bold text-center mb-2 text-yellow-300">🎲 勝利理由</h3>
                <p className="text-center text-gray-200 whitespace-pre-line">
                  {gameState.win_reason_description}
                </p>
              </div>
            )}

            {/* 最終スコア表示 */}
            <div className="bg-gray-900 rounded-lg p-6 mb-6 border border-gray-700">
              <h3 className="text-lg font-bold text-center mb-4 text-gray-300">最終スコア</h3>
              <div className="space-y-3">
                {/* 青チームスコア */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className={`w-4 h-4 rounded-full ${gameState.winner_team === 0 ? 'bg-yellow-400' : 'bg-blue-600'}`}></div>
                    <span className="font-bold text-blue-400">青チーム (P0+P2)</span>
                  </div>
                  <span className="text-2xl font-bold text-blue-400">{getPlayer(0).score}点</span>
                </div>

                {/* 赤チームスコア */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className={`w-4 h-4 rounded-full ${gameState.winner_team === 1 ? 'bg-yellow-400' : 'bg-red-600'}`}></div>
                    <span className="font-bold text-red-400">赤チーム (P1+P3)</span>
                  </div>
                  <span className="text-2xl font-bold text-red-400">{getPlayer(1).score}点</span>
                </div>
              </div>
            </div>

            {/* 新しいゲームボタン */}
            <div className="flex gap-3">
              <button
                onClick={() => handleNewGame(false)}
                className="flex-1 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-bold py-4 px-6 rounded-lg text-xl transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                新しいゲーム
              </button>
              <button
                onClick={() => handleNewGame(true)}
                className="flex-1 bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white font-bold py-4 px-6 rounded-lg text-xl transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                🔍 デバッグモード
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 5しモーダル */}
      {gameState.shi_5_players && gameState.shi_5_players.length > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 border-4 border-orange-400 rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl">
            {/* タイトル */}
            <div className="text-center mb-6">
              <h2 className="text-4xl font-bold text-orange-400 mb-2">⚠️ 「し」5枚 ⚠️</h2>
            </div>

            {/* 5shiプレイヤー表示 */}
            <div className="bg-gradient-to-r from-orange-900/30 to-orange-800/30 rounded-lg p-4 mb-6 border border-orange-600">
              <h3 className="text-lg font-bold text-center mb-3 text-orange-300">該当プレイヤー</h3>
              <div className="space-y-2">
                {gameState.shi_5_players.map((pos) => {
                  const player = getPlayer(pos);
                  return (
                    <div key={pos} className="flex items-center justify-between bg-gray-900/50 rounded p-2">
                      <span className="text-gray-200 font-bold">{player.name} (P{pos})</span>
                      <span className="text-orange-400 font-bold">し × 5</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* 配り直し選択（通常モード）またはAI判断結果表示（デバッグモード） */}
            {gameState.needs_redistribution_decision ? (
              <div className="space-y-3">
                <p className="text-center text-gray-300 mb-4">
                  配り直しますか？
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => redistributionDecision(true)}
                    className="flex-1 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-bold py-4 px-6 rounded-lg text-xl transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
                  >
                    配り直す
                  </button>
                  <button
                    onClick={() => redistributionDecision(false)}
                    className="flex-1 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-bold py-4 px-6 rounded-lg text-xl transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
                  >
                    続行
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-center text-gray-300 mb-4">
                  {gameState.debug_mode ? 'AIが判断しました' : 'ゲームを続行します'}
                </p>
                <button
                  onClick={() => {
                    // 情報表示のみの場合、何もしない（自動的に次のフェーズに進む）
                  }}
                  className="w-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-bold py-4 px-6 rounded-lg text-xl transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
                >
                  OK
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 手役（6し/7し/8し）モーダル */}
      {gameState.teyaku_player !== null && gameState.teyaku_player !== undefined && gameState.teyaku_shi_count && !teyakuModalDismissed && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 border-4 border-yellow-400 rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl">
            {/* タイトル */}
            <div className="text-center mb-6">
              <h2 className="text-4xl font-bold text-yellow-400 mb-4">✨ 手役 ✨</h2>
              <div className="text-5xl font-bold text-yellow-300 mb-2">
                「し」{gameState.teyaku_shi_count}枚
              </div>
            </div>

            {/* 手役プレイヤー表示 */}
            <div className="bg-gradient-to-r from-yellow-900/30 to-yellow-800/30 rounded-lg p-6 mb-6 border border-yellow-600">
              <h3 className="text-lg font-bold text-center mb-4 text-yellow-300">該当プレイヤー</h3>
              <div className="flex items-center justify-between bg-gray-900/50 rounded p-4">
                <span className="text-gray-200 font-bold text-xl">
                  {getPlayer(gameState.teyaku_player).name} (P{gameState.teyaku_player})
                </span>
                <span className="text-yellow-400 font-bold text-xl">し × {gameState.teyaku_shi_count}</span>
              </div>
            </div>

            {/* 説明テキスト */}
            <div className="bg-gray-900 rounded-lg p-4 mb-6 border border-gray-700">
              <p className="text-center text-gray-300">
                手役が発生しました。<br />
                {gameState.teyaku_shi_count === 8 && 'し8枚による即座ゲーム勝利！（150点）'}
                {gameState.teyaku_shi_count === 7 && 'し7枚によるラウンド勝利！'}
                {gameState.teyaku_shi_count === 6 && 'し6枚によるラウンド勝利！'}
              </p>
            </div>

            {/* OKボタン */}
            <button
              onClick={() => {
                // モーダルを閉じる（ローカルステートで制御）
                setTeyakuModalDismissed(true);
              }}
              className="w-full bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-white font-bold py-4 px-6 rounded-lg text-xl transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
